import { supabase } from '@/integrations/supabase/client';
import {
  queueAll, queueUpdate, queueDelete, type PendingOp,
} from './offline-db';

let syncing = false;

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/**
 * Tries to flush all pending operations to Supabase.
 * Detects conflicts by comparing remote state with the optimistic baseSnapshot.
 */
export async function syncPending(): Promise<{ ok: number; conflicts: number; failed: number }> {
  if (syncing || !isOnline()) return { ok: 0, conflicts: 0, failed: 0 };
  syncing = true;
  let ok = 0, conflicts = 0, failed = 0;
  try {
    const ops = await queueAll();
    for (const op of ops) {
      if (op.status === 'conflict') { conflicts++; continue; }
      if (op.status === 'failed') { failed++; continue; }
      try {
        op.status = 'syncing'; op.attempts += 1;
        await queueUpdate(op);

        const result = await applyOp(op);
        if (result === 'conflict') {
          op.status = 'conflict';
          await queueUpdate(op);
          conflicts++;
        } else {
          await queueDelete(op.id);
          ok++;
        }
      } catch (e: any) {
        op.status = op.attempts >= 5 ? 'failed' : 'pending';
        op.lastError = e?.message || String(e);
        await queueUpdate(op);
        failed++;
      }
    }
  } finally {
    syncing = false;
  }
  return { ok, conflicts, failed };
}

async function applyOp(op: PendingOp): Promise<'ok' | 'conflict'> {
  const table = op.table as any;
  if (op.op === 'insert') {
    const { error } = await supabase.from(table).insert(op.payload);
    if (error) throw error;
    return 'ok';
  }
  if (op.op === 'update') {
    if (op.match?.id && op.baseSnapshot) {
      const { data: remote } = await supabase.from(table).select('*').eq('id', op.match.id).maybeSingle();
      if (remote && hasConflict(remote, op.baseSnapshot, op.payload)) {
        op.conflictRemote = remote;
        return 'conflict';
      }
    }
    let q: any = supabase.from(table).update(op.payload);
    for (const [k, v] of Object.entries(op.match || {})) q = q.eq(k, v);
    const { error } = await q;
    if (error) throw error;
    return 'ok';
  }
  if (op.op === 'delete') {
    let q: any = supabase.from(table).delete();
    for (const [k, v] of Object.entries(op.match || {})) q = q.eq(k, v);
    const { error } = await q;
    if (error) throw error;
    return 'ok';
  }
  return 'ok';
}

function hasConflict(remote: any, base: any, change: any): boolean {
  // Conflict: remote.updated_at/created_at differs from base AND change touches a field that remote also changed
  const ts = remote.updated_at || remote.created_at;
  const baseTs = base.updated_at || base.created_at;
  if (!ts || !baseTs) return false;
  if (ts === baseTs) return false;
  // Check if any updated key differs
  for (const k of Object.keys(change || {})) {
    if (k === 'updated_at') continue;
    if (JSON.stringify(remote[k]) !== JSON.stringify(base[k])) return true;
  }
  return false;
}

export async function resolveConflict(opId: string, choice: 'local' | 'remote') {
  const ops = await queueAll();
  const op = ops.find(o => o.id === opId);
  if (!op) return;
  if (choice === 'remote') {
    await queueDelete(opId);
    return;
  }
  // Force local: re-apply update without conflict check
  op.baseSnapshot = op.conflictRemote;
  op.status = 'pending';
  op.attempts = 0;
  op.conflictRemote = undefined;
  await queueUpdate(op);
  await syncPending();
}

let started = false;
export function startAutoSync() {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => { syncPending(); });
  // Periodic retry every 30s when online
  setInterval(() => { if (isOnline()) syncPending(); }, 30_000);
  // Initial flush
  setTimeout(() => { if (isOnline()) syncPending(); }, 1500);
}